import React, { useState, useEffect, useRef } from 'react';
import { useEditMode } from '../../context/EditModeContext';

interface EditableTextProps {
    contentKey: string;
    defaultValue: string;
    as?: keyof JSX.IntrinsicElements;
    className?: string;
    multiline?: boolean;
}

function EditableText({
    contentKey,
    defaultValue,
    as: Component = 'span',
    className = '',
    multiline = false,
}: EditableTextProps) {
    const { isEditMode, editedContent, updateEditedContent, getEditedValue } = useEditMode();
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(defaultValue);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    // Update local value when defaultValue changes (e.g., language switch)
    useEffect(() => {
        setValue(getEditedValue(contentKey, defaultValue));
    }, [defaultValue, contentKey, editedContent, getEditedValue]);

    const handleClick = () => {
        if (isEditMode && !isEditing) {
            setIsEditing(true);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value);
    };

    const handleBlur = () => {
        setIsEditing(false);
        updateEditedContent(contentKey, value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            setIsEditing(false);
            updateEditedContent(contentKey, value);
        }
        if (e.key === 'Escape') {
            setIsEditing(false);
            setValue(getEditedValue(contentKey, defaultValue));
        }
    };

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    if (isEditMode) {
        const editableClassName = `${className} ${isEditing ? 'border border-blue-400 p-1 rounded' : 'border border-dashed border-gray-300 p-1 hover:border-blue-300'
            }`;

        if (isEditing) {
            if (multiline) {
                return (
                    <textarea
                        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                        value={value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className={`${editableClassName} min-h-[100px] w-full`}
                        autoFocus
                    />
                );
            }
            return (
                <input
                    ref={inputRef as React.RefObject<HTMLInputElement>}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className={editableClassName}
                    autoFocus
                />
            );
        }

        return (
            <Component
                className={`${editableClassName} cursor-pointer`}
                onClick={handleClick}
                data-editable="true"
                data-content-key={contentKey}
            >
                {value}
            </Component>
        );
    }

    // When not in edit mode, display either the edited value or the default value
    const displayValue = getEditedValue(contentKey, defaultValue);
    return <Component className={className}>{displayValue}</Component>;
}

export default EditableText; 